import { useMemo, useState } from "react";
import { ImageIcon, X } from "lucide-react";
import { cn } from "@/modern/utils";
import { Dialog, DialogContent } from "@/modern/components/ui/dialog";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useFileUrl } from "@/react/components/entityViews/files/api/files.api";

export interface SelectorImage {
  id: string | number;
  url: string;
  thumbnailUrl?: string;
  alt?: string;
  /** When url/thumbnailUrl are empty, resolve display URL via GET /file/{id}/presigned-url. */
  fileId?: number;
}

interface FileSelectorModalProps {
  getImages: (project: any) => SelectorImage[];
  getSelectedImages?: () => (string | number)[];
  onConfirm?: (images: SelectorImage[]) => void;
  onRemove?: (id: string | number) => void;
  buttonLabel?: string;
  title?: string;
  description?: string;
  /** Shown when getImages returns nothing — explain why the list is empty. */
  emptyTitle?: string;
  emptyDescription?: string;
  onClose: () => void;
}

const SelectorImageThumb = ({ image }: { image: SelectorImage }) => {
  const needsPresigned = !image.thumbnailUrl && !image.url && image.fileId != null;
  const { displayUrl, isLoading } = useFileUrl(needsPresigned ? image.fileId : null);
  const src = image.thumbnailUrl || image.url || displayUrl || "";

  if (needsPresigned && isLoading) {
    return <Skeleton className="w-full h-full rounded-none" />;
  }

  if (!src) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-muted">
        <ImageIcon className="h-8 w-8 text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={image.alt ?? ""}
      className="w-full h-full object-cover"
    />
  );
};

export const FileSelectorModal = ({
  getImages,
  getSelectedImages,
  onConfirm,
  onRemove,
  buttonLabel = "Confirm",
  title = "Select Images",
  description = "Browse and select images",
  emptyTitle = "No images available",
  emptyDescription = "There are no images to select from.",
  onClose,
}: FileSelectorModalProps) => {
  const { project } = useSwivuBryntumContext();

  // Compute images and pre-selected IDs once on mount.
  const allImages = useMemo<SelectorImage[]>(
    () => (project ? getImages(project) : []),
    [],
  );

  const preSelectedIds = useMemo<Set<string | number>>(
    () => new Set(getSelectedImages?.() ?? []),
    [],
  );

  const [newlySelectedIds, setNewlySelectedIds] = useState<Set<string | number>>(new Set());
  // IDs the user has marked for removal — committed only on confirm.
  const [pendingRemoveIds, setPendingRemoveIds] = useState<Set<string | number>>(new Set());

  const toggleImage = (id: string | number) => {
    setNewlySelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const togglePendingRemove = (id: string | number) => {
    setPendingRemoveIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    pendingRemoveIds.forEach((id) => onRemove?.(id));
    const selected = allImages.filter((img) => newlySelectedIds.has(img.id));
    onConfirm?.(selected);
    onClose();
  };

  const hasChanges = newlySelectedIds.size > 0 || pendingRemoveIds.size > 0;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-[900px] h-[80vh] p-0 gap-0 flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-border bg-background">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-sm font-semibold text-foreground leading-tight">{title}</h2>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-md text-muted-foreground hover:text-foreground"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Image Grid */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {allImages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center px-8 py-16">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-dashed border-border bg-muted/40">
                <ImageIcon className="h-8 w-8 text-muted-foreground/40" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-foreground">{emptyTitle}</p>
                <p className="text-xs text-muted-foreground max-w-[320px] leading-relaxed">
                  {emptyDescription}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {allImages.map((image) => {
                const isPreSelected = preSelectedIds.has(image.id);
                const isPendingRemove = pendingRemoveIds.has(image.id);
                const isNewlySelected = newlySelectedIds.has(image.id);
                const isChecked = isPreSelected ? !isPendingRemove : isNewlySelected;

                return (
                  <div
                    key={image.id}
                    onClick={() =>
                      isPreSelected
                        ? onRemove && togglePendingRemove(image.id)
                        : toggleImage(image.id)
                    }
                    className={cn(
                      "relative group aspect-square rounded-xl border overflow-hidden transition-all duration-150 cursor-pointer",
                      isPendingRemove
                        ? "border-destructive/50 ring-2 ring-destructive/20 opacity-50"
                        : isPreSelected
                          ? "border-primary/40 ring-2 ring-primary/20 opacity-70"
                          : isNewlySelected
                            ? "border-primary ring-2 ring-primary/30 shadow-md shadow-primary/10"
                            : "border-border/50 bg-card hover:border-primary/40 hover:shadow-sm",
                    )}
                  >
                    <SelectorImageThumb image={image} />

                    {/* Unified checkbox — top-left for all states */}
                    <div className="absolute top-2 left-2 z-10">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() =>
                          isPreSelected
                            ? onRemove && togglePendingRemove(image.id)
                            : toggleImage(image.id)
                        }
                        onClick={(e) => e.stopPropagation()}
                        className={cn(
                          "shadow",
                          isPendingRemove && "border-destructive data-[state=unchecked]:border-destructive",
                        )}
                      />
                    </div>

                    {/* Selection / pending-remove tint */}
                    {(isNewlySelected || isPendingRemove) && (
                      <div
                        className={cn(
                          "absolute inset-0 pointer-events-none rounded-xl",
                          isPendingRemove ? "bg-destructive/10" : "bg-primary/5",
                        )}
                      />
                    )}

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-xl" />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 flex items-center justify-between px-5 py-3 border-t border-border bg-muted/40">
          <div className="flex items-center gap-3">
            {newlySelectedIds.size > 0 && (
              <span className="text-xs text-muted-foreground">
                {newlySelectedIds.size} image{newlySelectedIds.size !== 1 ? "s" : ""} to add
              </span>
            )}
            {pendingRemoveIds.size > 0 && (
              <span className="text-xs text-destructive">
                {pendingRemoveIds.size} image{pendingRemoveIds.size !== 1 ? "s" : ""} to remove
              </span>
            )}
            {!hasChanges && (
              <span className="text-xs text-muted-foreground">No changes</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-8 px-3 text-xs" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-8 px-3 text-xs"
              disabled={!hasChanges}
              onClick={handleConfirm}
            >
              {buttonLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
